import React, { useMemo, useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';

import { hasValidInput } from 'components/Model/ModelUtils';
import ModelType from 'components/Model/ModelType';
import ModelsContainer from 'components/Model/ModelsContainer';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';
import createQueryRequest from 'services/createQueryRequest';
import { useTranslation } from 'localization/useTranslation';


const createProviderOption = (provider) => ({ value: provider, text: provider.displayName || provider.id, description: provider.description || provider.id });
const createProviderClientTemplateOption = (template) => ({ value: template, text: template.displayName, description: template.id });


const providerClientIdHasError = (value) => {
  if (_.isEmpty(value)) {
    return true;
  }
  return !value.match(/^[0-9a-zA-Z][0-9a-zA-Z_-]*[0-9a-zA-Z]$/g);
};


const DataSourceProvider = ({ assetInfo, setAssetInfo, providers, isLoadingProviders, setIsValid }) => {
  const T = useTranslation();
  const api = useAPI();

  const [providerClientExists, setProviderClientExists] = useState(false);
  const [providerClientTemplates, setProviderClientTemplates] = useState([]);


  const defaultQuery = useQuery(['defaultTemplate', { tagFilter: 'assetModel=default' }],
    createQueryRequest(api.core.getProviderClientTemplates),
    {
      onSuccess: (templates) => {
        const dOptions = _.map(templates, template => createProviderClientTemplateOption(template));
        const aOptions = _.map(assetModelQuery?.data, template => createProviderClientTemplateOption(template));
        const pOptions = _.map(providerQuery?.data, template => createProviderClientTemplateOption(template));
        const options = [
          ...aOptions,
          ...pOptions,
          ...dOptions
        ];
        setProviderClientTemplates(options);
      }
    }
  );

  const providerQuery = useQuery(['providerTemplates', { tagFilter: `providerType=${assetInfo?.providerClient?.provider?.type}` }],
    createQueryRequest(api.core.getProviderClientTemplates),
    {
      enabled: !_.isEmpty(assetInfo?.providerClient?.provider?.type),
      onSuccess: (templates) => {
        const pOptions = _.map(templates, template => createProviderClientTemplateOption(template));
        const aOptions = _.map(assetModelQuery?.data, template => createProviderClientTemplateOption(template));
        const dOptions = _.map(defaultQuery?.data, template => createProviderClientTemplateOption(template));
        const options = [
          ...aOptions,
          ...pOptions,
          ...dOptions
        ];
        setProviderClientTemplates(options);
      }
    }
  );

  const assetModelQuery = useQuery(['assetModelTemplates', { tagFilter: `assetModel=${assetInfo?.asset?.model?.id}` }],
    createQueryRequest(api.core.getProviderClientTemplates),
    {
      enabled: !_.isEmpty(assetInfo?.asset?.model?.id),
      onSuccess: (templates) => {
        const aOptions = _.map(templates, template => createProviderClientTemplateOption(template));
        const pOptions = _.map(providerQuery?.data, template => createProviderClientTemplateOption(template));
        const dOptions = _.map(defaultQuery?.data, template => createProviderClientTemplateOption(template));
        const options = [
          ...aOptions,
          ...pOptions,
          ...dOptions
        ];
        setProviderClientTemplates(options);
      }
    }
  );

  const [checkProviderClientId] = useApiCall({
    apiCall: api.core.getProviderClient,
    onSuccess: useCallback(() => {
      setProviderClientExists(true);
    }, []),
    onError: useCallback(() => {
      setProviderClientExists(false);
    }, [])
  });

  const providerClientErrorText = useCallback((value) => {
    const error = providerClientIdHasError(value);
    if (error) {
      return T.asset.idFormat.invalid;
    }
    if (providerClientExists) {
      return T.common.idExists;
    }
    return '';
  }, [T.asset.idFormat.invalid, T.common.idExists, providerClientExists]);

  const providerClientError = useCallback((value) => {
    return providerClientIdHasError(value) || (providerClientExists && !assetInfo.linkToExistingProviderClient);
  }, [assetInfo.linkToExistingProviderClient, providerClientExists]);


  useEffect(function initiateProviderData() {
    if (_.isEmpty(assetInfo?.providerClient?.displayName)) {
      setAssetInfo(oldInfo => ({ ...oldInfo, providerClient: { displayName: oldInfo.asset.displayName, description: oldInfo.asset.description } }));
    }
  }, [assetInfo?.providerClient, setAssetInfo]);

  const providerModel = useMemo(() => {
    return _.compact([
      {
        type: ModelType.OPTIONSV2,
        label: T.common.dataProvider,
        path: 'providerClient.provider',
        isRequired: true,
        hasError: _.isEmpty,
        options: _.map(providers, createProviderOption),
        properties: {
          isLoading: isLoadingProviders,
          iteratee: _.iteratee('id')
        }
      },
      {
        type: ModelType.TEXT,
        label: T.common.physicalId,
        path: 'providerClient.id',
        isRequired: true,
        hasError: providerClientError,
        errorText: providerClientErrorText
      },
      providerClientExists && {
        type: ModelType.CHECKBOX,
        label: T.asset.create.linkExisting,
        path: 'linkToExistingProviderClient'
      },
      {
        type: ModelType.TEXT,
        label: T.common.displayname,
        path: 'providerClient.displayName',
        placeholder: assetInfo?.asset?.displayName,
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXTAREA,
        label: T.common.description,
        path: 'providerClient.description',
        isRequired: false
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.asset.provider.template,
        path: 'providerClient.template',
        isRequired: true,
        disabled: _.isEmpty(providerClientTemplates),
        options: providerClientTemplates,
        hasError: _.isEmpty,
        properties: {
          isLoading: defaultQuery.isLoading || assetModelQuery.isLoading,
          iteratee: _.iteratee('id'),
          isClearable: true
        }
      }
    ]);
  }, [T.asset.create.linkExisting, T.asset.provider.template, T.common.dataProvider, T.common.description, T.common.displayname, T.common.physicalId, assetInfo?.asset?.displayName, assetModelQuery.isLoading, defaultQuery.isLoading, isLoadingProviders, providerClientError, providerClientErrorText, providerClientExists, providerClientTemplates, providers]);

  useEffect(function validateInput () {
    setIsValid(hasValidInput(assetInfo, providerModel));
  }, [assetInfo, providerModel, setIsValid]);

  const debouncedCheckProviderClientId = useMemo(() => {
    return _.debounce((provider, providerClientId) => {
      checkProviderClientId(provider, providerClientId);
    }, 1000, { trailing: true });
  }, [checkProviderClientId]);

  useEffect(function checkExistingProviderClientId() {
    if (!_.isEmpty(assetInfo?.providerClient?.provider?.id)) {
      debouncedCheckProviderClientId(assetInfo?.providerClient?.provider?.id, assetInfo?.providerClient?.id);
    }
  }, [assetInfo?.providerClient?.id, assetInfo?.providerClient?.provider?.id, debouncedCheckProviderClientId]);

  return <ModelsContainer models={providerModel} data={assetInfo} setData={setAssetInfo} />;
};

export default DataSourceProvider;
